import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Check, BookOpen, Clock } from "lucide-react";
import { toast } from "sonner";

const PACKAGES = [
  {
    key: "essential",
    name: "Essential Counseling",
    price: "$75",
    unit: "per session",
    subtitle: "60-Minute Individual Session",
    tagline:
      "Perfect for those seeking biblical guidance through life's challenges.",
    includes: [
      "60-minute one-on-one counseling session",
      "Scripture-centered guidance",
      "Practical biblical application",
      "Prayer at the conclusion of each session",
      "Personalized action steps for spiritual growth",
    ],
    forHeading: "Topics May Include",
    forList: [
      "Anxiety & fear",
      "Depression & discouragement",
      "Spiritual growth",
      "Decision making",
      "Grief & loss",
      "Family struggles",
      "Anger & forgiveness",
      "Life transitions",
    ],
  },
  {
    key: "growth",
    name: "Growth Journey",
    price: "$250",
    unit: "four sessions",
    subtitle: "Four 60-Minute Sessions",
    tagline:
      "Designed for those ready to pursue lasting biblical transformation rather than simply finding temporary relief.",
    includes: [
      "Four private counseling sessions",
      "Personalized spiritual growth plan",
      "Scripture assignments between sessions",
      "Practical accountability",
      "Prayer and encouragement throughout the process",
      "Priority scheduling",
    ],
    forHeading: "Recommended For",
    forList: [
      "Ongoing personal struggles",
      "Marriage concerns",
      "Spiritual disciplines",
      "Emotional healing",
      "Relationship restoration",
      "Habitual sin patterns",
      "Long-term discipleship",
    ],
    savings: "Save $50 when purchasing the four-session package.",
    best: true,
  },
];

export default function IndividualCounseling() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    package: "essential",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries/individual", form);
      setSent(true);
      toast.success("Inquiry sent. Cody will be in touch soon.");
      setForm({ name: "", email: "", phone: "", package: "essential", reason: "" });
    } catch {
      toast.error("Could not send. Please try again or email thedisciplelifems@gmail.com");
    } finally {
      setBusy(false);
    }
  };

  const input = (key, label, type = "text", required = true) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>
        {label}{required && " *"}
      </label>
      <input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`ic-${key}-input`}
        className="w-full px-4 py-3 text-sm outline-none"
      />
    </div>
  );

  return (
    <div data-testid="individual-counseling-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pt-14 pb-10">
        <Link to="/counseling" data-testid="ic-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-8" style={{ color: "var(--muted)" }}>
          <ArrowLeft size={12} /> Back to counseling
        </Link>
        <div className="overline mb-4">Biblical Counseling · Individual</div>
        <h1 className="font-serif-display tracking-[-0.045em] leading-[0.9]" style={{ color: "var(--ink)" }}>
          <span className="block text-5xl sm:text-6xl lg:text-8xl">Scripture-grounded</span>
          <span className="block text-5xl sm:text-6xl lg:text-8xl">
            <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>one-on-one care.</span>
          </span>
        </h1>
        <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Individual counseling from a <strong style={{ color: "var(--ink)" }}>Certified Biblical Counselor</strong>. Two options —
          choose a single session when you need clarity, or the Growth Journey when you're ready to walk through something deeper.
        </p>
      </section>

      {/* Packages */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-24 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {PACKAGES.map((p) => (
          <article
            key={p.key}
            data-testid={`ic-package-${p.key}`}
            className="card-surface p-8 lg:p-10 relative flex flex-col"
            style={p.best ? { borderColor: "var(--ember)" } : {}}
          >
            {p.best && (
              <span className="absolute -top-3 left-8 px-3 py-1 text-[0.6rem] uppercase tracking-[0.22em]" style={{ background: "var(--ember)", color: "#FFFFFF", fontWeight: 700 }}>
                Best value
              </span>
            )}
            <div className="overline mb-3" style={{ fontSize: "0.6rem" }}>{p.subtitle}</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl leading-tight" style={{ color: "var(--ink)" }}>{p.name}</h2>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-serif-display text-5xl" style={{ color: "var(--ember)" }}>{p.price}</span>
              <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>{p.unit}</span>
            </div>
            <p className="mt-4 text-sm lg:text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>{p.tagline}</p>

            <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="overline mb-3" style={{ fontSize: "0.65rem" }}>Includes</div>
              <ul className="space-y-2">
                {p.includes.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--ink-2)" }}>
                    <Check size={14} strokeWidth={2.5} className="shrink-0 mt-1" style={{ color: "var(--ember)" }} /> {it}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="overline mb-3" style={{ fontSize: "0.65rem" }}>{p.forHeading}</div>
              <ul className="grid grid-cols-1 gap-1.5 text-sm" style={{ color: "var(--ink-2)" }}>
                {p.forList.map((it, i) => (
                  <li key={i}>· {it}</li>
                ))}
              </ul>
            </div>

            {p.savings && (
              <p className="mt-6 pt-6 border-t text-sm font-medium" style={{ borderColor: "var(--border-subtle)", color: "var(--olive)" }}>
                {p.savings}
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                setForm({ ...form, package: p.key });
                document.getElementById("ic-form")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid={`ic-select-${p.key}`}
              className="btn-primary mt-auto self-start mt-8"
            >
              Request this package →
            </button>
          </article>
        ))}
      </section>

      {/* Inquiry form */}
      <section id="ic-form" style={{ background: "var(--bg-alt)", borderTop: "1px solid var(--border-subtle)" }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
          {sent ? (
            <div className="text-center py-12" data-testid="ic-form-thanks">
              <BookOpen size={40} strokeWidth={1.5} className="mx-auto" style={{ color: "var(--ember)" }} />
              <h2 className="font-serif-display text-4xl mt-6" style={{ color: "var(--ink)" }}>Thank you.</h2>
              <p className="mt-4 max-w-xl mx-auto" style={{ color: "var(--ink-2)" }}>
                Your inquiry is in. I'll reach out personally within a day or two to talk through what you're walking through and set up our first session.
              </p>
              <Link to="/counseling" className="btn-ghost mt-8 inline-flex">Back to counseling</Link>
            </div>
          ) : (
            <>
              <div className="overline mb-3">Request a session</div>
              <h2 className="font-serif-display text-3xl lg:text-5xl leading-tight tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
                Reach out — I'll respond personally.
              </h2>
              <p className="mt-4" style={{ color: "var(--ink-2)" }}>
                A short note about what you're seeking counsel for helps me prepare and reach out with real thought.
              </p>

              <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="ic-inquiry-form">
                {input("name", "Your name")}
                {input("email", "Email", "email")}
                {input("phone", "Phone")}

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>Package *</label>
                  <select
                    required
                    value={form.package}
                    onChange={(e) => setForm({ ...form, package: e.target.value })}
                    data-testid="ic-package-select"
                    className="w-full px-4 py-3 text-sm outline-none"
                  >
                    <option value="essential">Essential — $75 per session</option>
                    <option value="growth">Growth Journey — $250 (4 sessions)</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>
                    Briefly, what would you like counsel for? *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    data-testid="ic-reason-input"
                    placeholder="A few sentences — anxiety, a decision you're wrestling with, grief, marriage, spiritual dryness…"
                    className="w-full px-4 py-3 text-sm outline-none"
                  />
                  <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
                    Kept confidential. Only Cody sees this.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="ic-submit-button"
                  className="btn-primary md:col-span-2 justify-self-start"
                >
                  <Clock size={14} /> {busy ? "Sending…" : "Send inquiry"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
